import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useLazyGetStockLotScanQuery } from 'store/slices/apiV1/inventory';
import { checkIfValidUUID } from 'utils/functions';
import { StockLotCRUDSchema } from 'types/inventory';
import useSnackbar from 'hooks/useSnackbar';
import { useScanner, ScannerProps } from 'hooks/useScanner';
import { v4 as uuidv4 } from 'uuid';
import { parseStockLocationCode } from 'utils/barcodeParsing';
import { useServerSideLazyQuery } from 'hooks/useServerSideLazyQuery';
import { useLazyGetStockLocationsClientV2Query } from 'store/slices/clientV2/stockLocations';
import {
  StockLocationSchema,
  StockLocationSchemaLite,
} from 'types/clientV2/stockLocations';

export const STOCK_LOT_LENGTH = 17;

type ScannerContextProps = {
  scannedStockLocation: StockLocationSchemaLite | null;
  stockLocationLoading: boolean;
  scannedStockLot: StockLotCRUDSchema | null;
  setScannedStockLocation: (
    stockLocation: StockLocationSchemaLite | null
  ) => void;
  setScannedStockLot: (stockLot: StockLotCRUDSchema | null) => void;
  stockLotLoading: boolean;
  setScannedStringHandler: (barcode: string) => void;
  setIsScanningStockLotLockedHandler: (isLocked: boolean) => void;
  setIsScanningStockLocationLockedHandler: (isLocked: boolean) => void;
  addAdditionalScanHandler: (key: string, scanHandler: ScanHandler) => void;
  removeAdditionalScanHandler: (key: string) => void;
  isScanningStockLotLocked: boolean;
  isScanningStockLocationLocked: boolean;
  additionalScanHandlersRef: React.MutableRefObject<AdditionalScanHandlers>;
  setIsScanningDisabledGloballyHandler: (
    isDisabled: boolean,
    contextKey: string
  ) => void;
  isScanningDisabledGlobally: {
    disabled: boolean;
    exclusiveContextKeys: Set<string>;
  };
  isScanning: boolean;
  setIsScanning: (isScanning: boolean) => void;
  exclusiveScanOnly: {
    enabled: boolean;
    exclusiveContextKeys: Set<string>;
  };
  setExclusiveScanOnlyHandler: (
    isExclusive: boolean,
    contextKey: string
  ) => void;
};

type ScannerProviderProps = ScannerProps & {
  children?: ReactNode;
};
type ScanHandler = (scannedString: string) => Promise<boolean>;
type AdditionalScanHandlers = {
  [key: string]: ScanHandler;
};

const ScannerContext = createContext<ScannerContextProps | undefined>(
  undefined
);

const keyJoiningCharacter = '_';

const generateHandlerKey = (key: string, contextKey: string) =>
  `${key}${keyJoiningCharacter}${contextKey}`;

const extractContextKey = (handlerKey: string) =>
  handlerKey.split(keyJoiningCharacter)[1];
const extractFunctionKey = (handlerKey: string) =>
  handlerKey.split(keyJoiningCharacter)[0];

export const ScannerProvider: FC<ScannerProviderProps> = ({
  children,
  ...props
}) => {
  const { scannedString, resetScannedString, setScannedStringHandler } =
    useScanner(props);
  const { dispatchErrorSnackbar } = useSnackbar();
  const [scannedStockLocation, setScannedStockLocation] =
    useState<StockLocationSchemaLite | null>(null);
  const [scannedStockLot, setScannedStockLot] =
    useState<StockLotCRUDSchema | null>(null);
  const [getStockLocation, { isLoading: stockLocationLoading }] =
    useServerSideLazyQuery<StockLocationSchema, StockLocationSchemaLite>(
      useLazyGetStockLocationsClientV2Query
    );
  const [getStockLot, { isLoading: stockLotLoading }] =
    useLazyGetStockLotScanQuery();
  const [isScanningStockLotLocked, setIsScanningStockLotLocked] =
    useState<boolean>(false);
  const [isScanningStockLocationLocked, setIsScanningStockLocationLocked] =
    useState<boolean>(false);
  const [isScanningDisabledGlobally, setIsScanningDisabledGlobally] = useState<{
    disabled: boolean;
    exclusiveContextKeys: Set<string>;
  }>({ disabled: false, exclusiveContextKeys: new Set() });
  const additionalScanHandlersRef = useRef<AdditionalScanHandlers>({});
  const [exclusiveScanOnly, setExclusiveScanOnly] = useState<{
    enabled: boolean;
    exclusiveContextKeys: Set<string>;
  }>({ enabled: false, exclusiveContextKeys: new Set() });
  const [isScanning, setIsScanning] = useState<boolean>(false);

  const exclusiveScanOnlyRef = useRef(exclusiveScanOnly);
  const isScanningDisabledGloballyRef = useRef(isScanningDisabledGlobally);

  useEffect(() => {
    exclusiveScanOnlyRef.current = exclusiveScanOnly;
  }, [exclusiveScanOnly]);

  useEffect(() => {
    isScanningDisabledGloballyRef.current = isScanningDisabledGlobally;
  }, [isScanningDisabledGlobally]);

  const setIsScanningStockLotLockedHandler = (isLocked: boolean) => {
    setIsScanningStockLotLocked(isLocked);
  };

  const setIsScanningStockLocationLockedHandler = (isLocked: boolean) => {
    setIsScanningStockLocationLocked(isLocked);
  };

  const setIsScanningDisabledGloballyHandler = (
    isDisabled: boolean,
    contextKey: string
  ) => {
    setIsScanningDisabledGlobally((prev) => {
      const newExclusiveContextKeys = new Set(prev.exclusiveContextKeys);
      if (isDisabled) {
        newExclusiveContextKeys.add(contextKey);
      } else {
        newExclusiveContextKeys.delete(contextKey);
      }
      return {
        disabled: newExclusiveContextKeys.size > 0,
        exclusiveContextKeys: newExclusiveContextKeys,
      };
    });
  };

  const setExclusiveScanOnlyHandler = useCallback(
    (isExclusive: boolean, contextKey: string) => {
      setExclusiveScanOnly((prev) => {
        const newExclusiveContextKeys = new Set(prev.exclusiveContextKeys);
        if (isExclusive) {
          newExclusiveContextKeys.add(contextKey);
        } else {
          newExclusiveContextKeys.delete(contextKey);
        }
        return {
          enabled: newExclusiveContextKeys.size > 0,
          exclusiveContextKeys: newExclusiveContextKeys,
        };
      });
    },
    []
  );

  const addAdditionalScanHandler = (key: string, scanHandler: ScanHandler) => {
    additionalScanHandlersRef.current = {
      ...additionalScanHandlersRef.current,
      [key]: scanHandler,
    };
  };

  const removeAdditionalScanHandler = (key: string) => {
    const newHandlers = { ...additionalScanHandlersRef.current };
    delete newHandlers[key];
    additionalScanHandlersRef.current = newHandlers;
  };

  const handleGetStockLot = useCallback(async (barcode: string) => {
    try {
      const stockLotResponse = await getStockLot({
        barcode,
        expand: true,
      }).unwrap();
      setScannedStockLot(stockLotResponse);
    } catch (err) {
      dispatchErrorSnackbar('Stock Lot Not Found');
      throw err;
    }
  }, []);

  const handleGetStockLocationUUID = useCallback(async (barcode: string) => {
    try {
      const locationResponse = await getStockLocation({
        pageSize: 1,
        filters: [
          {
            field: 'id',
            operator: '=',
            value: barcode,
          },
        ],
      }).unwrap();
      setScannedStockLocation(locationResponse.data?.[0] ?? null);
      if (!locationResponse.data?.[0]) {
        dispatchErrorSnackbar('Location Not Found');
        throw new Error('Location not found');
      }
    } catch (err) {
      dispatchErrorSnackbar('Location Not Found');
      throw err;
    }
  }, []);

  const handleGetStockLocationLocationCode = useCallback(
    async (barcode: string) => {
      try {
        const locationResponse = await getStockLocation({
          pageSize: 1,
          filters: [
            {
              field: 'stockLocationCode',
              operator: '=',
              value: barcode,
            },
          ],
        }).unwrap();
        setScannedStockLocation(locationResponse.data?.[0] ?? null);
        if (!locationResponse.data?.[0]) {
          dispatchErrorSnackbar('Location Not Found');
          throw new Error('Location not found');
        }
      } catch (err) {
        dispatchErrorSnackbar('Location Not Found');
        throw err;
      }
    },
    []
  );

  // TODO: Get printer (?) update slice for supporting id maybe not needed yet
  // https://linear.app/cofactr/issue/UI-3228/add-printer-scan-support-to-scannercontext

  const runCustomHandlers = useCallback(
    async (barcode: string) => {
      if (Object.keys(additionalScanHandlersRef.current).length > 0) {
        const handlerResults = await Promise.all(
          Object.entries(additionalScanHandlersRef.current).map(
            async ([key, handler]) => {
              const handlerContextKey = extractContextKey(key);
              if (
                isScanningDisabledGloballyRef.current.disabled &&
                !isScanningDisabledGloballyRef.current.exclusiveContextKeys.has(
                  handlerContextKey
                )
              ) {
                return false;
              }
              return handler(barcode);
            }
          )
        );
        if (handlerResults.every((result) => result === false)) {
          dispatchErrorSnackbar('Invalid Barcode');
        }
        return true;
      }
      return false;
    },
    [dispatchErrorSnackbar]
  );

  useEffect(() => {
    const handleScan = async () => {
      if (!scannedString.length) return;

      try {
        // Check if we should skip default handlers (exclusive mode)
        const shouldSkipDefaultHandlers =
          exclusiveScanOnlyRef.current.enabled ||
          (Object.keys(additionalScanHandlersRef.current).length > 0 &&
            Object.values(additionalScanHandlersRef.current).some(
              (_, index) => {
                const handlerKey = Object.keys(
                  additionalScanHandlersRef.current
                )[index];
                const handlerContextKey = extractContextKey(handlerKey);
                return isScanningDisabledGloballyRef.current.exclusiveContextKeys.has(
                  handlerContextKey
                );
              }
            ));

        // Run default handlers only if not in exclusive mode
        if (!shouldSkipDefaultHandlers) {
          if (scannedString.length === STOCK_LOT_LENGTH) {
            // Stock lot scanning involves a network request, so set isScanning
            setIsScanning(true);
            await handleGetStockLot(scannedString);
            return;
          }

          // Check for stock location code first
          const stockLocationCode = parseStockLocationCode(scannedString);
          if (stockLocationCode) {
            // Stock location scanning involves a network request, so set isScanning
            setIsScanning(true);
            await handleGetStockLocationLocationCode(stockLocationCode);
            return;
          }

          // Fallback to UUID check for backward compatibility
          if (checkIfValidUUID(scannedString)) {
            // UUID scanning involves a network request, so set isScanning
            setIsScanning(true);
            await handleGetStockLocationUUID(scannedString);
            return;
          }
        }

        // Run custom handlers (either in exclusive mode or as fallback)
        const customHandlersRan = await runCustomHandlers(scannedString);
        if (
          !customHandlersRan &&
          !shouldSkipDefaultHandlers &&
          !checkIfValidUUID(scannedString) &&
          !parseStockLocationCode(scannedString)
        ) {
          dispatchErrorSnackbar('Invalid Barcode');
        }
      } finally {
        setIsScanning(false);
        resetScannedString();
      }
    };

    handleScan();
  }, [
    scannedString,
    handleGetStockLot,
    handleGetStockLocationUUID,
    handleGetStockLocationLocationCode,
  ]);

  return (
    <ScannerContext.Provider
      value={{
        scannedStockLocation,
        scannedStockLot,
        setScannedStockLocation,
        setScannedStockLot,
        stockLocationLoading,
        stockLotLoading,
        setScannedStringHandler,
        setIsScanningStockLotLockedHandler,
        setIsScanningStockLocationLockedHandler,
        addAdditionalScanHandler,
        additionalScanHandlersRef,
        removeAdditionalScanHandler,
        isScanningStockLotLocked,
        isScanningStockLocationLocked,
        setIsScanningDisabledGloballyHandler,
        isScanningDisabledGlobally,
        isScanning,
        setIsScanning,
        exclusiveScanOnly,
        setExclusiveScanOnlyHandler,
      }}
    >
      {children}
    </ScannerContext.Provider>
  );
};

type UseScannerContextProps = {
  handleScannedStockLot?: (scannedStockLot: StockLotCRUDSchema) => void;
  handleScannedStockLocation?: (
    scannedStockLocation: StockLocationSchemaLite
  ) => void;
  isExclusiveScanStockLot?: boolean;
  isExclusiveScanStockLocation?: boolean;
  isExclusiveScanAll?: boolean;
  isExclusiveScanOnly?: boolean;
  handleOtherScans?: {
    [key: string]: ScanHandler;
  };
};

/**
 * Hook for accessing and interacting with the scanner context.
 *
 * @param props Configuration options for the scanner context
 * @returns Scanner context with additional utility properties
 *
 * @example
 * // Basic usage
 * const { isLoading } = useScannerContext({
 *   handleScannedStockLot: (stockLot) => {
 *     // Handle scanned stock lot
 *   }
 * });
 *
 * @example
 * // Using custom scan handlers with loading state
 * const { setIsScanning } = useScannerContext({
 *   handleOtherScans: {
 *     customScan: async (barcode) => {
 *       try {
 *         setIsScanning(true); // Show loading indicator
 *         // Perform async operation (e.g., API call)
 *         await someApiCall(barcode);
 *         return true; // Scan was handled successfully
 *       } catch (error) {
 *         return false; // Scan handling failed
 *       } finally {
 *         setIsScanning(false); // Hide loading indicator
 *       }
 *     }
 *   }
 * });
 */
export const useScannerContext = (props: UseScannerContextProps = {}) => {
  const {
    handleScannedStockLot,
    handleScannedStockLocation,
    isExclusiveScanStockLocation = false,
    isExclusiveScanStockLot = false,
    handleOtherScans,
    isExclusiveScanAll = false,
    isExclusiveScanOnly = false,
  } = props;
  const context = useContext(ScannerContext);
  const uniqueContextKey = useRef<string>(uuidv4());

  if (context === undefined) {
    throw new Error('useScannerContext must be used within a ScannerProvider');
  }

  const {
    scannedStockLot,
    scannedStockLocation,
    setScannedStockLocation,
    setScannedStockLot,
    setIsScanningStockLocationLockedHandler,
    setIsScanningStockLotLockedHandler,
    isScanningStockLotLocked,
    isScanningStockLocationLocked,
    addAdditionalScanHandler,
    additionalScanHandlersRef,
    removeAdditionalScanHandler,
    isScanningDisabledGlobally,
    setIsScanningDisabledGloballyHandler,
    stockLocationLoading,
    stockLotLoading,
    isScanning,
    exclusiveScanOnly,
    setExclusiveScanOnlyHandler,
  } = context;

  useEffect(() => {
    setExclusiveScanOnlyHandler(isExclusiveScanOnly, uniqueContextKey.current);
    return () => {
      setExclusiveScanOnlyHandler(false, uniqueContextKey.current);
    };
  }, [isExclusiveScanOnly, setExclusiveScanOnlyHandler]);

  useEffect(() => {
    setIsScanningDisabledGloballyHandler(
      isExclusiveScanAll,
      uniqueContextKey.current
    );

    return () => {
      setIsScanningDisabledGloballyHandler(false, uniqueContextKey.current);
    };
  }, [isExclusiveScanAll]);

  useEffect(() => {
    if (isExclusiveScanStockLocation) {
      setIsScanningStockLocationLockedHandler(true);
    }
    if (isExclusiveScanStockLot) {
      setIsScanningStockLotLockedHandler(true);
    }
    return () => {
      if (isExclusiveScanStockLocation) {
        setIsScanningStockLocationLockedHandler(false);
      }
      if (isExclusiveScanStockLot) {
        setIsScanningStockLotLockedHandler(false);
      }
    };
  }, [isExclusiveScanStockLocation, isExclusiveScanStockLot]);

  const isAllowedToScan = useMemo(
    () =>
      !isScanningDisabledGlobally.disabled ||
      isScanningDisabledGlobally.exclusiveContextKeys.has(
        uniqueContextKey.current
      ),
    [isScanningDisabledGlobally, uniqueContextKey]
  );

  useEffect(() => {
    if (!isAllowedToScan) {
      return;
    }
    if (exclusiveScanOnly.enabled) {
      return;
    }
    if (isScanningStockLotLocked && !isExclusiveScanStockLot) {
      return;
    }
    if (scannedStockLot && handleScannedStockLot) {
      handleScannedStockLot(scannedStockLot);
      setScannedStockLot(null);
    }
  }, [
    scannedStockLot,
    isAllowedToScan,
    exclusiveScanOnly,
    isScanningStockLotLocked,
    isExclusiveScanStockLot,
    handleScannedStockLot,
  ]);

  useEffect(() => {
    if (!isAllowedToScan) {
      return;
    }
    if (exclusiveScanOnly.enabled) {
      return;
    }
    if (isScanningStockLocationLocked && !isExclusiveScanStockLocation) {
      return;
    }
    if (scannedStockLocation && handleScannedStockLocation) {
      handleScannedStockLocation(scannedStockLocation);
      setScannedStockLocation(null);
    }
  }, [
    scannedStockLocation,
    isAllowedToScan,
    exclusiveScanOnly,
    isScanningStockLocationLocked,
    isExclusiveScanStockLocation,
    handleScannedStockLocation,
  ]);

  useEffect(() => {
    if (!handleOtherScans) return () => {};

    Object.entries(handleOtherScans).forEach(([key, handler]) => {
      addAdditionalScanHandler(
        generateHandlerKey(key, uniqueContextKey.current),
        handler
      );
    });

    return () => {
      Object.keys(additionalScanHandlersRef.current).forEach((handlerKey) => {
        const functionKey = extractFunctionKey(handlerKey);
        if (!Object.keys(handleOtherScans).includes(functionKey)) {
          removeAdditionalScanHandler(handlerKey);
        }
      });
    };
  }, [handleOtherScans]);

  // Clean up handlers
  useEffect(
    () => () => {
      if (!handleOtherScans) return;
      Object.keys(handleOtherScans).forEach((key) => {
        removeAdditionalScanHandler(
          generateHandlerKey(key, uniqueContextKey.current)
        );
      });
    },
    []
  );

  return {
    ...context,
    isLoading: isScanning || stockLocationLoading || stockLotLoading,
  };
};
